import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ImageCarousel.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showThumbnails: false,
  showFullscreenButton: false,
  slideDuration: 350,
};

/**
 * The ImageCarousel component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array?} props.optimizedImages - Array of optimized images from listing metadata
 * @returns {JSX.Element} image carousel component
 */
const ImageCarousel = props => {
  const [currentIndex, setIndex] = useState(0);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, optimizedImages, listing } = props;

  // Check for video
  const videoUrl = listing?.attributes?.metadata?.videos?.mergedVideo?.url;
  const firstImage = images?.[0];

  // Build items array - video first if available, then images
  const imageItems = images.map((img, i) => {
    return {
      original: '',
      alt: intl.formatMessage(
        { id: 'ImageCarousel.imageAltText' },
        { index: i + 1, count: images.length + (videoUrl ? 1 : 0) }
      ),
      image: img,
      isVideo: false,
    };
  });

  const videoItem = videoUrl
    ? {
        original: '',
        alt: intl.formatMessage({ id: 'ImageCarousel.videoAltText' }),
        videoUrl: videoUrl,
        image: firstImage, // Use first image as fallback
        isVideo: true,
      }
    : null;

  const items = videoItem ? [videoItem, ...imageItems] : imageItems;

  const renderItem = item => {
    if (item.isVideo && item.videoUrl) {
      return (
        <div className={css.imageWrapper}>
          <div className={css.itemCentering}>
            <video
              className={css.videoItem}
              src={item.videoUrl}
              controls
              loop
              autoPlay={currentIndex === 0}
            />
          </div>
        </div>
      );
    }

    // Find the image index in the original images array
    const imageIndex = images.findIndex(img => img.id === item.image.id);
    return (
      <div className={css.imageWrapper}>
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            sizes="(max-width: 767px) 100vw, 80vw"
            optimizedImages={optimizedImages}
            imageIndex={imageIndex >= 0 ? imageIndex : undefined}
          />
        </div>
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" className={css.arrowHead} />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" className={css.arrowHead} />
        </div>
      </button>
    );
  };

  // If no image is given, rendere empty image.
  if (items.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={classes} image={null} variants={[]} alt="" />;
  }

  const naturalIndex = index => index + 1;

  // Render image index info. E.g. "4/12"
  const imageIndex =
    items.length > 0 ? (
      <span className={css.imageIndex}>
        {naturalIndex(currentIndex)}/{items.length}
      </span>
    ) : null;

  // Update index when slide changes
  const handleSlide = slideIndex => {
    setIndex(slideIndex);
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onSlide={handleSlide}
        startIndex={0}
        {...IMAGE_GALLERY_OPTIONS}
      />
      {imageIndex}
    </>
  );
};

export default ImageCarousel;
